import type { GameTab } from '../../engine/tabs';
import { showToast } from '../../engine/toast';
import { authStore } from '../../core/state/auth-store';
import {
  fetchLeaderboard,
  fetchMyWorldId,
  fetchPersonalRank,
  fetchWorldRanking,
} from './leaderboard-api';
import type { LeaderboardEntry, PersonalRank, WorldRankingEntry } from './leaderboard-api';
import { t } from '../../engine/i18n/i18n';

const CATEGORY = 'colony-power';
const MAX_ROWS = 25;

function escapeHtml(value: string): string {
  const div = document.createElement('div');
  div.textContent = value;
  return div.innerHTML;
}

export function formatPoints(value: number): string {
  return value.toLocaleString('en-GB');
}

type TabId = 'all' | 'world';

/**
 * The two boards answer different questions and come from different
 * endpoints: "All Worlds" ranks PLAYERS by their power summed across every
 * colony they hold (core/leaderboard), "This World" ranks the COLONIES in
 * the player's own world (game/leaderboard-feed). They used to render the
 * same array, so switching tabs changed nothing but the button styling.
 *
 * Desktop shows both at once — that is the point of the side-by-side layout.
 * Below `lg` there is no room for two tables, so the tab bar comes back and
 * picks one.
 */
interface Panel {
  id: TabId;
  label: () => string;
  caption: () => string;
}

const PANELS: Panel[] = [
  { id: 'all', label: () => t('leaderboard.allWorlds'), caption: () => t('leaderboard.allWorlds.caption') },
  { id: 'world', label: () => t('leaderboard.thisWorld'), caption: () => t('leaderboard.thisWorld.caption') },
];

export function renderLeaderboardScreen(root: HTMLElement): () => void {
  root.innerHTML = `
    <div id="leaderboard-screen" class="w-full h-full overflow-y-auto overscroll-contain">
      <div class="text-center text-text-dim py-10">${t('leaderboard.loading')}</div>
    </div>
  `;
  const container = root.querySelector('#leaderboard-screen') as HTMLDivElement;

  let disposed = false;
  const currentUserId = authStore.get().user?.id ?? null;

  let rank: PersonalRank | null = null;
  let rankFailed = false;
  let entries: LeaderboardEntry[] = [];
  let entriesFailed = false;
  let worldEntries: WorldRankingEntry[] = [];
  let worldFailed = false;
  /** null once resolved means the account holds no colony, so there is no "this world". */
  let worldId: string | null = null;
  /** The player's main colony, shown on their standing card. */
  let colonyName: string | null = null;
  let activeTab: TabId = 'all';

  function personalRankCardHtml(): string {
    if (rankFailed) {
      return `
        <section class="glass-panel px-4 py-4" data-personal-rank>
          <p class="text-xs text-text-dim">${t('leaderboard.standingUnavailable')}</p>
        </section>
      `;
    }
    if (!rank) {
      return `
        <section class="glass-panel px-4 py-4" data-personal-rank>
          <p class="text-xs text-text-dim">${t('leaderboard.standingLoading')}</p>
        </section>
      `;
    }
    const playerName = rank.username ?? authStore.get().user?.username ?? '—';
    return `
      <section class="glass-panel px-4 py-4 flex items-center gap-4" data-personal-rank>
        <div class="rank-emblem w-[5.25rem] h-[5.75rem] text-xl shrink-0" data-personal-rank-position>#${rank.rank}</div>
        <div class="min-w-0 flex-1">
          <div class="text-xs text-text-dim">${t('leaderboard.rankOf', { rank: rank.rank, total: formatPoints(rank.total) })}</div>
          <div class="mt-0.5 flex items-baseline gap-1.5">
            <span class="text-lg text-gold-bright font-bold tabular-nums">${formatPoints(rank.value)}</span>
            <span class="text-[10px] text-text-dim uppercase tracking-wide">${t('profile.colonyPower')}</span>
          </div>
        </div>
        <!-- Who you are sits on the right, where the card used to be empty: player name over main colony name. -->
        <div class="min-w-0 max-w-[55%] shrink-0 text-right" data-personal-identity>
          <div class="text-base text-text font-bold truncate" data-personal-name>${escapeHtml(playerName)}</div>
          ${colonyName ? `<div class="mt-0.5 text-xs text-gold truncate" data-personal-colony>${escapeHtml(colonyName)}</div>` : ''}
        </div>
      </section>
    `;
  }

  /** Mobile only — desktop shows both panels, so there is nothing to switch. */
  function tabBarHtml(): string {
    return `
      <div class="flex gap-1.5 lg:hidden">
        ${PANELS.map(
          (p) => `
          <button type="button" data-action="switch-tab" data-tab="${p.id}"
            class="min-h-9 px-3 rounded-full text-xs font-heading uppercase tracking-wide transition-colors duration-[120ms] touch-manipulation ${
              p.id === activeTab
                ? 'bg-gold/20 text-gold border border-gold/40'
                : 'bg-surface-2 text-text-dim border border-border hover:text-text hover:border-gold/30'
            }">${p.label()}</button>`,
        ).join('')}
      </div>
    `;
  }

  function rowHtml(opts: {
    rank: number;
    userId: string;
    primary: string | null;
    secondary?: string | null;
    value: number;
  }): string {
    const isMe = currentUserId !== null && opts.userId === currentUserId;
    const topTier = opts.rank <= 3;
    const name = opts.primary ? escapeHtml(opts.primary) : t('leaderboard.anonymous');
    const sub = opts.secondary
      ? `<span class="block text-[10px] text-text-dim truncate">${escapeHtml(opts.secondary)}</span>`
      : '';
    return `
      <li data-leaderboard-row data-user-id="${escapeHtml(opts.userId)}"
        class="flex items-center gap-3 px-3 py-2.5 border-b border-border/40 last:border-0 ${isMe ? 'ring-1 ring-gold/60 bg-gold/10 rounded' : ''}">
        <span class="w-10 shrink-0 text-right font-heading text-sm tabular-nums ${topTier ? 'text-gold-bright' : 'text-text-dim'}">#${opts.rank}</span>
        <span class="flex-1 min-w-0 text-sm text-text">
          <span class="block truncate">${name}${isMe ? ' <span class="text-[9px] text-gold uppercase tracking-wide">' + t('leaderboard.you') + '</span>' : ''}</span>
          ${sub}
        </span>
        <span class="shrink-0 text-sm tabular-nums ${topTier ? 'text-gold-bright font-bold' : 'text-text'}">
          ${formatPoints(opts.value)} <span class="text-[9px] text-text-dim uppercase">${t('leaderboard.cp')}</span>
        </span>
      </li>
    `;
  }

  function ellipsisRowHtml(): string {
    return `<li class="text-center text-text-dim text-xs py-2">&hellip;</li>`;
  }

  function emptyHtml(message: string): string {
    return `<p class="text-xs text-text-dim px-1">${message}</p>`;
  }

  /**
   * Renders `rows` as-is UNLESS it is exactly one entry longer than
   * `MAX_ROWS` — the one shape both `entries`/`worldEntries` can take when
   * the player ranks below the visible cut (see `appendMissingSelf` below
   * and `LeaderboardFeedService.getWorldRanking`, which does the equivalent
   * append server-side for the world board). That extra trailing entry
   * carries the player's TRUE rank, not `MAX_ROWS + 1`, so it renders after
   * an ellipsis rather than as a false continuation of the list above it.
   */
  function withPlayerRow<T>(rows: T[], render: (row: T) => string): string {
    if (rows.length <= MAX_ROWS) return rows.map(render).join('');
    const mine = rows[rows.length - 1];
    return rows.slice(0, MAX_ROWS).map(render).join('') + ellipsisRowHtml() + render(mine);
  }

  /** Appends `mine` as an extra trailing row when it exists and isn't already in `rows` — see `withPlayerRow`. */
  function appendMissingSelf<T extends { userId: string }>(rows: T[], mine: T | null): T[] {
    if (!mine || rows.some((r) => r.userId === mine.userId)) return rows;
    return [...rows, mine];
  }

  function allWorldsListHtml(): string {
    if (entriesFailed) return emptyHtml(t('leaderboard.error.all'));
    if (entries.length === 0) return emptyHtml(t('leaderboard.empty.all'));
    const mine: LeaderboardEntry | null =
      rank && currentUserId
        ? { rank: rank.rank, userId: rank.userId, username: rank.username, value: rank.value }
        : null;
    const rows = withPlayerRow(appendMissingSelf(entries, mine), (e) =>
      rowHtml({ rank: e.rank, userId: e.userId, primary: e.username, value: e.value }),
    );
    return `<ul class="glass-panel flex flex-col overflow-hidden" data-leaderboard-list>${rows}</ul>`;
  }

  function worldListHtml(): string {
    if (worldFailed) return emptyHtml(t('leaderboard.error.world'));
    if (!worldId) return emptyHtml(t('leaderboard.empty.noColony'));
    if (worldEntries.length === 0) return emptyHtml(t('leaderboard.empty.world'));
    const rows = withPlayerRow(worldEntries, (e) =>
      rowHtml({
        rank: e.rank,
        userId: e.userId,
        primary: e.colonyName,
        secondary: e.username,
        value: e.value,
      }),
    );
    return `<ul class="glass-panel flex flex-col overflow-hidden" data-leaderboard-list-world>${rows}</ul>`;
  }

  function panelHtml(panel: Panel, body: string): string {
    // `hidden lg:flex` keeps the inactive panel in the DOM but out of the way
    // on mobile; at lg both are always shown and the tab state is irrelevant.
    const mobileVisibility = panel.id === activeTab ? 'flex' : 'hidden lg:flex';
    return `
      <section class="${mobileVisibility} flex-col gap-2 min-w-0" data-panel="${panel.id}">
        <div class="hidden lg:block">
          <h2 class="font-heading text-sm text-text">${panel.label()}</h2>
          <p class="text-[10px] text-text-dim">${panel.caption()}</p>
        </div>
        ${body}
      </section>
    `;
  }

  function render(): void {
    container.innerHTML = `
      <div class="max-w-2xl lg:max-w-5xl mx-auto p-4 sm:p-6 pb-12 flex flex-col gap-4">
        <div>
          <h1 class="font-heading text-gold text-lg">${t('nav.rankings')}</h1>
          <p class="text-[10px] text-text-dim uppercase tracking-[0.2em] mt-0.5">${t('profile.colonyPower')}</p>
        </div>
        ${personalRankCardHtml()}
        ${tabBarHtml()}
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4 lg:gap-6 items-start" data-list-section>
          ${panelHtml(PANELS[0], allWorldsListHtml())}
          ${panelHtml(PANELS[1], worldListHtml())}
        </div>
      </div>
    `;
  }

  async function loadInitial(): Promise<void> {
    // The world board needs a worldId first, so it is a second hop. Everything
    // else goes out together.
    const [rankResult, entriesResult, colonyResult] = await Promise.allSettled([
      fetchPersonalRank(),
      fetchLeaderboard(CATEGORY, MAX_ROWS, 0),
      fetchMyWorldId(),
    ]);
    if (disposed) return;

    if (rankResult.status === 'fulfilled') rank = rankResult.value;
    else rankFailed = true;

    if (entriesResult.status === 'fulfilled') {
      entries = entriesResult.value;
    } else {
      entriesFailed = true;
      const message = entriesResult.reason instanceof Error ? entriesResult.reason.message : t('leaderboard.toast.loadFailed');
      showToast(message, 'error');
    }

    // A colonyless account legitimately has no world — not an error, and it
    // must not raise a toast.
    worldId = colonyResult.status === 'fulfilled' ? colonyResult.value.worldId : null;
    colonyName = colonyResult.status === 'fulfilled' ? (colonyResult.value.name ?? null) : null;

    render();

    if (!worldId) return;
    try {
      const rows = await fetchWorldRanking(worldId, MAX_ROWS);
      if (disposed) return;
      worldEntries = rows;
    } catch {
      if (disposed) return;
      worldFailed = true;
    }
    render();
  }

  function onClick(e: MouseEvent): void {
    const target = e.target as HTMLElement;
    const actionEl = target.closest('[data-action]') as HTMLElement | null;
    if (!actionEl) return;
    if (actionEl.dataset.action === 'switch-tab') {
      const tab = actionEl.dataset.tab as TabId;
      if (tab !== activeTab) {
        activeTab = tab;
        render();
      }
    }
  }
  container.addEventListener('click', onClick);

  void loadInitial();

  return () => {
    disposed = true;
    container.removeEventListener('click', onClick);
  };
}

export const leaderboardTab: GameTab = {
  id: 'leaderboard',
  label: t('nav.rankings'),
  icon: 'leaderboard',
  mount: renderLeaderboardScreen,
};
